import { Icon } from "materia"
import { useTranslation } from "react-i18next"
import { modifiers } from "ui-tools"
import { FiestaImage } from "utility-components"
import type { SharedItem } from "../../types"
import s from "./SharedProductCard.module.scss"

/**
 * The four states a shared learning item resolves through (FAME-237 / FAME-453):
 * resolving while the batch lookup is in flight, accessible once it comes back,
 * noAccess when the caller cannot open it (absent from the response), and
 * fallback when there is nothing to resolve against but the sender's deep link.
 */
export type ShareCardState =
  | { status: "resolving" }
  | { status: "accessible"; item: SharedItem; onOpen: () => void }
  | { status: "noAccess" }
  | { status: "fallback"; url: string }

type SharedProductCardProps = {
  /** Ours sits in the blue bubble, so the card inverts to read on it. */
  isOwn: boolean
  state: ShareCardState
}

export function SharedProductCard({ isOwn, state }: SharedProductCardProps) {
  const { t } = useTranslation()

  if (state.status === "resolving") {
    return (
      <div
        className={modifiers(s, "card", "resolving", { isOwn })}
        aria-busy="true"
      >
        <div className={s.card__image} />
        <div className={s.card__footer}>
          <div className={s.card__skeletonLine} />
        </div>
      </div>
    )
  }

  if (state.status === "noAccess") {
    return (
      <div className={modifiers(s, "card", "noAccess", { isOwn })}>
        <div className={s.card__lock}>
          <Icon icon="Lock" size="large" />
        </div>
        <p className={s.card__noAccessText}>{t("chat.SHARE_NO_ACCESS")}</p>
      </div>
    )
  }

  if (state.status === "fallback") {
    return (
      <a
        className={modifiers(s, "card", "fallback", { isOwn })}
        href={state.url}
        target="_blank"
        rel="noopener noreferrer nofollow"
      >
        <Icon icon="Attachment" size="small" />
        <span className={s.card__fallbackText}>
          {t("chat.SHARE_OPEN_LINK")}
        </span>
      </a>
    )
  }

  const { item, onOpen } = state
  return (
    <button
      type="button"
      className={modifiers(s, "card", { isOwn })}
      onClick={onOpen}
    >
      <FiestaImage
        className={s.card__image}
        src={item.thumbnail ?? undefined}
        alt=""
        loading="lazy"
        sizes="240px"
      />
      <div className={s.card__footer}>
        <div className={s.card__text}>
          <span className={s.card__type}>
            {t(`product.PRODUCT_TYPES.${item.productType}`)}
          </span>
          <span className={s.card__title}>{item.title}</span>
        </div>
        <Icon className={s.card__open} icon="ArrowChunky" size="small" />
      </div>
    </button>
  )
}
